"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
const HERO_IMAGE_URL = "https://ideal-jay.10web.cloud/wp-content/uploads/2026/08/teelistclub-membership-hero-golfer-sunrise.png";
export default function MembershipJoinHero() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.08
  });
  const scrollToJoinForm = () => {
    const formSection = document.getElementById("join-teelistclub-form");
    if (formSection) {
      formSection.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1607" id="membershipjoinhero" aria-labelledby="membershipjoinhero-title" className="relative w-full overflow-hidden bg-[#F7F3E9] text-[#073F31]">
      <div className="grid w-full grid-cols-1 lg:min-h-[850px] lg:grid-cols-[46%_54%]">
        {/* Left hero content */}
        <div className="relative z-20 flex min-h-[620px] flex-col items-start justify-center px-5 py-16 sm:px-8 md:px-12 lg:min-h-[850px] lg:px-16 lg:py-24 xl:px-20 2xl:px-24">
          <div className="w-full max-w-[590px]">
            <div ref={eyebrowRef} className={`mb-5 lg:mt-[100px] transition-all duration-700 ease-out ${eyebrowVisible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}>
              <span className="text-sm font-bold uppercase tracking-[0.24em] text-[#B88F46]" style={{
              fontFamily: "'Raleway', Arial, sans-serif"
            }}>
                Join TeeListClub
              </span>
            </div>

            <h1 ref={headlineRef} id="membershipjoinhero-title" className={`max-w-[570px] font-normal leading-[1.04] tracking-[-0.04em] text-[#073F31] transition-all duration-700 ease-out lg:leading-[80px] ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(3.25rem, 4.6vw, 5.35rem)",
            transitionDelay: "80ms"
          }}>
              Build your
              <br />

              <em className="font-normal text-[#0B5A45]">
                golf life
              </em>

              <br />
              across Asia.
            </h1>

            <div aria-hidden="true" className={`mt-7 h-px w-12 bg-[#B88F46] transition-all duration-700 ease-out ${headlineVisible ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"}`} style={{
            transformOrigin: "left",
            transitionDelay: "180ms"
          }} />

            <p ref={bodyRef} className={`mt-7 max-w-[525px] text-[15px] font-normal leading-[1.8] text-[#4A6860] transition-all duration-700 ease-out sm:text-[16px] ${bodyVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
            fontFamily: "'Raleway', Arial, sans-serif",
            transitionDelay: "160ms"
          }}>
              Save courses you love, build future trip shortlists and receive
              more relevant golf and destination inspiration from TeeListClub.
            </p>

            <div ref={ctaRef} className={`mt-9 flex flex-col items-start gap-4 transition-all duration-700 ease-out sm:flex-row sm:items-center sm:gap-6 ${ctaVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
            fontFamily: "'Raleway', Arial, sans-serif",
            transitionDelay: "240ms"
          }}>
              <button type="button" onClick={scrollToJoinForm} className="group inline-flex min-h-[52px] items-center justify-center gap-3 bg-[#BFFF00] px-7 py-3 text-[13px] font-bold uppercase tracking-[0.08em] text-[#073F31] shadow-[0_10px_28px_rgba(7,63,49,0.12)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B3EF00] hover:shadow-[0_14px_34px_rgba(7,63,49,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#073F31] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F3E9] rounded-xl">
                Join TeeListClub

                <ArrowRight aria-hidden="true" className="size-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
              </button>

              <a href="https://ideal-jay.10web.cloud/course-finder/" className="group inline-flex min-h-[46px] items-center gap-3 border-b border-[#B8A789] px-0 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-[#073F31] transition-colors duration-300 hover:border-[#073F31]">
                Explore Course Finder

                <ArrowRight aria-hidden="true" className="size-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.7} />
              </a>
            </div>
          </div>
        </div>

        {/* Right hero image */}
        <div ref={imageRef} className={`relative min-h-[460px] overflow-hidden transition-opacity duration-1000 ease-out sm:min-h-[540px] lg:min-h-[850px] ${imageVisible ? "opacity-100" : "opacity-0"}`}>
          <img src={HERO_IMAGE_URL} alt="Golfer playing at sunrise on a scenic golf course" loading="eager" decoding="async" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover" style={{
          objectPosition: "12% center"
        }} />

          {/* Warm ivory fade only — no dark overlay */}
          <div aria-hidden="true" className="absolute inset-y-0 left-0 hidden w-[30%] bg-gradient-to-r from-[#F7F3E9] via-[#F7F3E9]/65 to-transparent lg:block" />

          <div aria-hidden="true" className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#F7F3E9]/25 to-transparent lg:hidden" />
        </div>
      </div>
    </section>;
}